import {
  Bot,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CornerLeftUp,
  ExternalLink,
  MessageSquare,
  MessageSquareWarning,
  PanelBottom,
  PanelRight,
  RotateCcw,
  TriangleAlert,
  X,
} from "lucide-react";
import { type ReactNode, type RefObject, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { projectHomePath } from "../router.tsx";
import { textAreaClass } from "../tasks/parts.tsx";
import { ThreadEntries } from "./AgentReplies.tsx";
import { ResolveStep, useResolvedLine } from "./ResolveNote.tsx";
import type { Flag, ReviewEntry } from "./ReviewReader.tsx";
import { CommentView, DeleteCommentDialog, type ThreadLike } from "./ThreadPanel.tsx";
import { useThreadWrite } from "./threads.ts";

// The companion pane (wiki-collaboration-ux D15, the user's concept C): one review item at a time
// beside the page column, or in the bottom sheet below 1024 px or when docked to the bottom. The
// same body in both; the draft lives with the caller so a dock switch keeps it.

export type Dock = "side" | "bottom";

export interface PaneItemProps {
  projectId: string;
  setId: string;
  entry: ReviewEntry;
  pageTitle: string;
  /** The heading the passage sits under, as the page reads it. */
  section: string | null;
  /** Jump to the passage, when it reads on the page. */
  onJump: (() => void) | null;
  draft: string;
  onDraft: (text: string) => void;
  canComment: boolean;
  /** "Ask the agent to fix it": the set is a conversation's and the viewer not its creator. */
  agentEligible: boolean;
  /** Posts a thread on the flagged sentence; the pane then shows it. */
  onFlagThread: (
    flag: Flag,
    kind: "comment" | "change_request",
    body: string,
    forAgent: boolean,
  ) => Promise<unknown>;
  /**
   * A Changed agent reply's "See the change" (wiki-resolve-with-agent D7, amended): the page in
   * Marked view, since the viewer's last review when they have marks, at the passage.
   */
  onSeeChange?: (() => void) | null;
}

export interface PaneFrameProps {
  /** ‹ N of M open ›: this item's place among the page's open items, null when it is not one. */
  step: { index: number; count: number } | null;
  onStep: (direction: -1 | 1) => void;
  onClose: () => void;
  dock: Dock;
  /** The pane fits (≥ 1024 px): the dock switch is offered. */
  dockable: boolean;
  onDock: (dock: Dock) => void;
  titleRef: RefObject<HTMLHeadingElement | null>;
}

/** The item's kind as its title: "Possible contradiction", "Question"… */
function useKindTitle(entry: ReviewEntry | null): string {
  const { t } = useTranslation();
  if (!entry) {
    return "";
  }
  return entry.type === "flag"
    ? t("wiki.review.line.contradiction")
    : t(`wiki.threads.kind.${entry.thread.kind}`);
}

/** The stepper over the page's open items: ‹ N of M open ›, or the item's kind alone. */
function Stepper(props: {
  step: PaneFrameProps["step"];
  onStep: PaneFrameProps["onStep"];
  label: string;
}) {
  const { t } = useTranslation();
  if (!props.step) {
    return null;
  }
  const { index, count } = props.step;
  return (
    <div className="flex items-center rounded-md border border-line bg-surface">
      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-r-none"
        aria-label={t("wiki.review.stepper.previous")}
        disabled={count < 2}
        onClick={() => props.onStep(-1)}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>
      <span
        aria-live="polite"
        className="flex items-center gap-1.5 border-x border-line px-2 text-[13px] font-medium whitespace-nowrap text-warning-fg tabular-nums"
      >
        <MessageSquareWarning aria-hidden="true" className="size-3.5" />
        {t("wiki.review.stepper.of", { index: index + 1, count })}
        <span className="sr-only">: {props.label}</span>
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-l-none"
        aria-label={t("wiki.review.stepper.next")}
        disabled={count < 2}
        onClick={() => props.onStep(1)}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
    </div>
  );
}

function DockSwitch(props: { dock: Dock; onDock: (dock: Dock) => void }) {
  const { t } = useTranslation();
  const next: Dock = props.dock === "side" ? "bottom" : "side";
  const Icon = next === "bottom" ? PanelBottom : PanelRight;
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      data-dock-switch={next}
      aria-label={t(`wiki.review.dock.${next}`)}
      title={t(`wiki.review.dock.${next}`)}
      onClick={() => props.onDock(next)}
    >
      <Icon aria-hidden="true" />
    </Button>
  );
}

/** The pane beside the page column (≥ 1024 px, docked to the side): 360 px, 400 px from 1280. */
export function ReviewPane(props: PaneItemProps & PaneFrameProps) {
  const kind = useKindTitle(props.entry);
  return (
    <CompanionPane {...props} kind={kind}>
      <PaneItem key={props.entry.key} {...props} />
    </CompanionPane>
  );
}

/**
 * The companion pane's frame (D15), whatever it holds: the stepper, the dock switch and Close
 * over the item. The review's items and a published page's threads (S4) use it alike.
 */
export function CompanionPane(
  props: Omit<PaneFrameProps, "titleRef"> & { kind: string; label?: string; children: ReactNode },
) {
  const { t } = useTranslation();
  return (
    <aside
      data-review-pane=""
      data-esi-content="wiki"
      aria-label={props.label ?? t("wiki.review.pane.label")}
      className="flex min-h-0 w-[360px] shrink-0 flex-col border-l border-line bg-surface-raised xl:w-[400px]"
    >
      <div className="flex h-bar shrink-0 items-center gap-2 border-b border-line pr-2.5 pl-3.5">
        <Stepper step={props.step} onStep={props.onStep} label={props.kind} />
        <span className="flex-1" />
        {props.dockable ? <DockSwitch dock="side" onDock={props.onDock} /> : null}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t("wiki.review.pane.close")}
          onClick={props.onClose}
        >
          <X aria-hidden="true" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-3.5 pb-4">{props.children}</div>
    </aside>
  );
}

/**
 * The bottom sheet (below 1024 px, or docked to the bottom): a slim bar with the stepper, the
 * item's title line and the dock switch, the item at 62% of the height when expanded, and the
 * decision bar as its footer. The page keeps scrolling above it.
 */
export function ReviewSheet(
  props: Omit<Partial<PaneItemProps>, "entry"> &
    Omit<PaneFrameProps, "titleRef"> & {
      titleRef: RefObject<HTMLHeadingElement | null>;
      entry: ReviewEntry | null;
      /** The page's open items when no item is open: opens the first. */
      openCount: number;
      onOpenFirst: () => void;
      expanded: boolean;
      onExpanded: (expanded: boolean) => void;
      bar: ReactNode;
    },
) {
  const { entry } = props;
  const kind = useKindTitle(entry);
  const item = entry && props.projectId && props.setId ? (props as PaneItemProps) : null;
  return (
    <CompanionSheet {...props} open={!!entry} kind={kind}>
      {item ? <PaneItem key={item.entry.key} {...item} titleRef={props.titleRef} /> : null}
    </CompanionSheet>
  );
}

/** The bottom sheet's frame (D15), whatever it holds; nothing at all with nothing to show. */
export function CompanionSheet(
  props: Omit<PaneFrameProps, "titleRef"> & {
    /** An item is open in it. */
    open: boolean;
    kind: string;
    label?: string;
    openCount: number;
    onOpenFirst: () => void;
    expanded: boolean;
    onExpanded: (expanded: boolean) => void;
    bar: ReactNode;
    children: ReactNode;
  },
) {
  const { t } = useTranslation();
  const { open, kind } = props;
  if (!open && !props.bar && !props.openCount) {
    return null;
  }
  return (
    <section
      data-review-sheet=""
      data-esi-content="wiki"
      aria-label={props.label ?? t("wiki.review.pane.label")}
      className={cn(
        "flex shrink-0 flex-col rounded-t-xl border-t border-line bg-surface-raised shadow-(--shadow-overlay)",
        open && props.expanded ? "h-[62%]" : "",
      )}
    >
      <div className="flex min-h-11 shrink-0 items-center gap-2 px-3 py-1.5">
        {open ? (
          <>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-expanded={props.expanded}
              aria-label={
                props.expanded ? t("wiki.review.sheet.collapse") : t("wiki.review.sheet.expand")
              }
              onClick={() => props.onExpanded(!props.expanded)}
            >
              {props.expanded ? (
                <ChevronDown aria-hidden="true" />
              ) : (
                <ChevronUp aria-hidden="true" />
              )}
            </Button>
            <Stepper step={props.step} onStep={props.onStep} label={kind} />
            <span className="min-w-0 flex-1 truncate text-[13px] text-fg-secondary">
              {props.step ? null : kind}
            </span>
          </>
        ) : props.openCount ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="text-warning-fg"
              onClick={props.onOpenFirst}
            >
              <MessageSquareWarning aria-hidden="true" />
              {t("wiki.review.stepper.open", { count: props.openCount })}
            </Button>
            <span className="flex-1" />
          </>
        ) : (
          <span className="flex-1" />
        )}
        {props.dockable && open ? <DockSwitch dock="bottom" onDock={props.onDock} /> : null}
        {open ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("wiki.review.pane.close")}
            onClick={props.onClose}
          >
            <X aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {open && props.expanded && props.children ? (
        <div className="min-h-0 flex-1 overflow-y-auto border-t border-line px-4 pt-3 pb-4">
          {/* A reading measure on a wide screen docked to the bottom. */}
          <div className="mx-auto w-full max-w-[44rem]">{props.children}</div>
        </div>
      ) : null}
      {props.bar ? <div className="shrink-0 border-t border-line">{props.bar}</div> : null}
    </section>
  );
}

/** The item itself: a thread with its composer, or the contradiction (D7). */
function PaneItem(props: PaneItemProps & { titleRef: RefObject<HTMLHeadingElement | null> }) {
  return props.entry.type === "flag" ? <FlagItem {...props} /> : <ThreadItem {...props} />;
}

function Where(props: {
  quote: string | null;
  section: string | null;
  pageTitle: string;
  onJump: (() => void) | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs break-words text-fg-muted">
        {props.quote ? (
          <>
            {t("wiki.review.pane.on")} “<span className="text-fg-secondary">{props.quote}</span>”
            {" · "}
          </>
        ) : props.onJump ? null : (
          <>{t("wiki.review.pane.onPage")} · </>
        )}
        {props.section ? `${props.section} · ` : ""}
        {props.pageTitle}
      </p>
      {props.onJump ? (
        <button
          type="button"
          onClick={props.onJump}
          className="flex w-fit items-center gap-1 rounded-sm text-[13px] font-medium text-accent-text hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <CornerLeftUp aria-hidden="true" className="size-3.5" />
          {t("wiki.review.pane.jump")}
        </button>
      ) : null}
    </div>
  );
}

function ThreadItem(props: PaneItemProps & { titleRef: RefObject<HTMLHeadingElement | null> }) {
  const write = useThreadWrite(props.projectId, props.setId);
  if (props.entry.type !== "thread") {
    return null;
  }
  const { thread, blocking } = props.entry;
  return (
    <PaneThread
      thread={thread}
      blocking={blocking}
      quote={thread.anchor?.quote ?? null}
      section={props.section}
      pageTitle={props.pageTitle}
      onJump={props.onJump}
      draft={props.draft}
      onDraft={props.onDraft}
      canComment={props.canComment}
      titleRef={props.titleRef}
      busy={write.isPending}
      error={write.isError ? write.error.message : null}
      onReply={(body) => write.mutateAsync({ kind: "reply", threadId: thread.id, body })}
      onResolve={(note) => write.mutateAsync({ kind: "resolve", threadId: thread.id, note })}
      onReopen={() => write.mutateAsync({ kind: "reopen", threadId: thread.id })}
      onDelete={(commentId) =>
        write.mutateAsync({ kind: "delete", threadId: thread.id, commentId })
      }
      onSeeChange={props.onSeeChange ?? null}
    />
  );
}

/** What the pane needs of a thread: review threads and page threads (S4) alike. */
export type PaneThreadLike = ThreadLike & {
  id: string;
  createdByName: string | null;
};

/**
 * One thread in the companion pane (D15): its kind, who and replies, Resolve or Reopen, where it
 * sits with Jump to the passage, the comments, and the reply composer with Reply & resolve.
 * Resolving asks for the optional one-line decision note first (D16). The draft lives with the
 * caller so a dock switch keeps it.
 */
export function PaneThread(props: {
  thread: PaneThreadLike;
  blocking: boolean;
  quote: string | null;
  section: string | null;
  pageTitle: string;
  onJump: (() => void) | null;
  draft: string;
  onDraft: (text: string) => void;
  canComment: boolean;
  titleRef: RefObject<HTMLHeadingElement | null>;
  busy: boolean;
  error: string | null;
  onReply: (body: string) => Promise<unknown>;
  onResolve: (note: string | undefined) => Promise<unknown>;
  onReopen: () => Promise<unknown>;
  onDelete: (commentId: string) => Promise<unknown>;
  /** The composer's line on who sees a reply; the review's own by default. */
  visibleLine?: string;
  /** Who resolves it, for a viewer who may not; the review's own by default. */
  whoResolves?: string;
  /** A line under where it sits: "Detached · on version 4" on a page (S4). */
  extra?: ReactNode;
  /** Actions after the comments: "Ask the agent" on a page thread. */
  footer?: ReactNode;
  /** "See the change" of a Changed agent reply (wiki-resolve-with-agent D7). */
  onSeeChange?: (() => void) | null;
}) {
  const { t } = useTranslation();
  const id = useId();
  const resolvedLine = useResolvedLine();
  const [remove, setRemove] = useState<string | null>(null);
  // null, or the resolve step: alone, or with the draft posted first ("Reply & resolve").
  const [resolving, setResolving] = useState<null | "alone" | "withReply">(null);
  const { thread, blocking } = props;
  const resolved = thread.status === "resolved";
  const replies = Math.max(0, thread.comments.filter((c) => !c.system).length - 1);
  const Icon = thread.kind === "change_request" ? MessageSquareWarning : MessageSquare;
  const reply = async () => {
    const body = props.draft.trim();
    if (body) {
      await props.onReply(body);
    }
    props.onDraft("");
  };
  const tooLong = Array.from(props.draft).length > 2000;
  const visible = props.visibleLine ?? t("wiki.review.pane.visible");
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3
            ref={props.titleRef}
            tabIndex={-1}
            className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[13px] outline-none"
          >
            <span
              className={cn(
                "flex items-center gap-1.5 font-medium",
                blocking ? "text-warning-fg" : "text-fg",
              )}
            >
              <Icon aria-hidden="true" className="size-3.5" />
              {thread.kind === "change_request"
                ? t("wiki.review.line.request")
                : t(`wiki.threads.kind.${thread.kind}`)}
            </span>
            <span className="text-fg-secondary">
              · {thread.createdByName ?? t("wiki.threads.someone")} ·{" "}
              {t("wiki.review.pane.replies", { count: replies })}
            </span>
            {thread.forAgent ? (
              <span className="flex items-center gap-1 text-fg-muted">
                · <Bot aria-hidden="true" className="size-3.5" />
                {t("wiki.threads.askedAgent")}
              </span>
            ) : null}
          </h3>
          {thread.mayResolve && !resolving ? (
            <Button
              size="xs"
              variant="ghost"
              disabled={props.busy}
              onClick={() =>
                resolved ? void props.onReopen().catch(() => {}) : setResolving("alone")
              }
            >
              {resolved ? <RotateCcw aria-hidden="true" /> : <Check aria-hidden="true" />}
              {resolved ? t("wiki.threads.reopen") : t("wiki.threads.resolve")}
            </Button>
          ) : null}
        </div>
        {resolved ? (
          <p data-esi-content="wiki" className="text-xs break-words text-fg-muted">
            {resolvedLine(thread)}
          </p>
        ) : null}
        <Where
          quote={props.quote}
          section={props.section}
          pageTitle={props.pageTitle}
          onJump={props.onJump}
        />
        {props.extra}
      </div>
      {resolving && !resolved ? (
        <ResolveStep
          busy={props.busy}
          withReply={resolving === "withReply"}
          onCancel={() => setResolving(null)}
          onResolve={async (note) => {
            if (resolving === "withReply") {
              await reply();
            }
            await props.onResolve(note);
            setResolving(null);
          }}
        />
      ) : null}
      <ThreadEntries
        className="flex flex-col gap-3.5"
        comments={thread.comments}
        agentReplies={thread.agentReplies}
        reviewer={{ userId: thread.createdBy ?? "", name: thread.createdByName }}
        mayReply={thread.mayReply && props.canComment}
        busy={props.busy}
        onPick={(choice) => void props.onReply(choice).catch(() => {})}
        onAnswer={() => document.getElementById(`${id}-reply`)?.focus()}
        onSeeChange={props.onSeeChange ? () => props.onSeeChange?.() : null}
        comment={(comment) => (
          <CommentView
            key={comment.id}
            comment={comment}
            busy={props.busy}
            onDelete={(commentId) => setRemove(commentId)}
          />
        )}
      />
      {props.footer}
      {!resolved && !thread.mayResolve ? (
        <p className="text-xs text-fg-muted">
          {props.whoResolves ?? t("wiki.review.pane.whoResolves")}
        </p>
      ) : null}
      {thread.mayReply && props.canComment && resolving !== "withReply" ? (
        <form
          className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-3 focus-within:border-line-strong"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await reply();
            } catch {
              // Shown below.
            }
          }}
        >
          <Label htmlFor={`${id}-reply`} className="sr-only">
            {t("wiki.threads.replyLabel")}
          </Label>
          <textarea
            id={`${id}-reply`}
            value={props.draft}
            onChange={(e) => props.onDraft(e.target.value)}
            placeholder={t("wiki.review.pane.replyPlaceholder")}
            disabled={props.busy}
            aria-invalid={tooLong ? true : undefined}
            aria-describedby={`${id}-who`}
            className={cn(
              textAreaClass,
              "min-h-16 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0",
            )}
          />
          <p id={`${id}-who`} className="text-xs text-fg-muted">
            {visible}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={props.busy || !props.draft.trim() || tooLong}
            >
              <MessageSquare aria-hidden="true" />
              {t("wiki.threads.reply")}
            </Button>
            {thread.mayResolve && !resolved ? (
              <Button
                type="button"
                size="sm"
                disabled={props.busy || !props.draft.trim() || tooLong}
                onClick={() => setResolving("withReply")}
              >
                <Check aria-hidden="true" />
                {t("wiki.review.pane.replyResolve")}
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}
      {props.error && !remove ? <InlineError>{props.error}</InlineError> : null}
      {remove ? (
        <DeleteCommentDialog
          busy={props.busy}
          error={props.error}
          onClose={() => setRemove(null)}
          onDelete={() => props.onDelete(remove)}
        />
      ) : null}
    </div>
  );
}

function FlagItem(props: PaneItemProps & { titleRef: RefObject<HTMLHeadingElement | null> }) {
  const { t } = useTranslation();
  const id = useId();
  const [composing, setComposing] = useState<"comment" | "agent" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (props.entry.type !== "flag") {
    return null;
  }
  const { flag, source } = props.entry;
  const tooLong = Array.from(props.draft).length > 2000;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h3
          ref={props.titleRef}
          tabIndex={-1}
          className="flex items-center gap-1.5 text-[13px] font-medium text-warning-fg outline-none"
        >
          <TriangleAlert aria-hidden="true" className="size-3.5" />
          {t("wiki.review.line.contradiction")}
        </h3>
        <Where
          quote={null}
          section={props.section}
          pageTitle={props.pageTitle}
          onJump={props.onJump}
        />
      </div>
      <div className="flex flex-col gap-3">
        {flag.statement ? (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-fg-muted">{t("wiki.review.pane.pageSays")}</p>
            <blockquote className="rounded-md border border-line bg-surface px-3 py-2 text-sm break-words text-fg">
              {flag.statement}
            </blockquote>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-fg-muted">
            {source
              ? t("wiki.review.pane.sourceSaysNamed", { title: source.title })
              : t("wiki.review.pane.sourceSays")}
          </p>
          <blockquote className="rounded-md border border-warning-border/40 bg-warning-bg/50 px-3 py-2 text-sm break-words text-fg">
            {flag.explanation}
          </blockquote>
          {source ? (
            <a
              href={`${projectHomePath(props.projectId, "files")}?document=${encodeURIComponent(source.id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-fit items-center gap-1 text-[13px] font-medium text-accent-text hover:underline"
            >
              <ExternalLink aria-hidden="true" className="size-3.5" />
              {t("wiki.review.pane.openSource")}
            </a>
          ) : null}
        </div>
      </div>
      <p className="text-xs text-fg-secondary">{t("wiki.review.pane.acknowledges")}</p>
      {props.canComment && flag.statement ? (
        composing ? (
          <form
            className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-3"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                await props.onFlagThread(
                  flag,
                  composing === "agent" ? "change_request" : "comment",
                  props.draft.trim(),
                  composing === "agent",
                );
                props.onDraft("");
                setComposing(null);
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            <Label htmlFor={`${id}-body`} className="text-[13px] font-medium">
              {composing === "agent"
                ? t("wiki.review.pane.agentLabel")
                : t("wiki.review.pane.commentLabel")}
            </Label>
            <textarea
              id={`${id}-body`}
              value={props.draft}
              onChange={(e) => props.onDraft(e.target.value)}
              disabled={busy}
              aria-invalid={tooLong ? true : undefined}
              className={cn(textAreaClass, "min-h-16")}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setComposing(null)}
              >
                {t("app.cancel")}
              </Button>
              <Button
                type="submit"
                size="sm"
                variant="outline"
                disabled={busy || !props.draft.trim() || tooLong}
              >
                {composing === "agent" ? (
                  <Bot aria-hidden="true" />
                ) : (
                  <MessageSquare aria-hidden="true" />
                )}
                {t("wiki.threads.post")}
              </Button>
            </div>
            {error ? <InlineError>{error}</InlineError> : null}
          </form>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setComposing("comment")}>
              <MessageSquare aria-hidden="true" />
              {t("wiki.review.pane.commentOn")}
            </Button>
            {props.agentEligible ? (
              <Button size="sm" variant="ghost" onClick={() => setComposing("agent")}>
                <Bot aria-hidden="true" />
                {t("wiki.review.pane.askAgent")}
              </Button>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );
}
